import React, { Dispatch, FormEvent, SetStateAction, useEffect, useMemo, useCallback, useState } from 'react';
import { useHistory } from 'react-router-dom';
import styles from './AddModal.module.scss';
import { Customer, SelectOption } from '../../../../types';
import Modal from '../../../../components/modal/Modal';
import Button from '../../../../components/button/Button';
import { TextInput, TextInputValue } from '../../../../components/text-input/TextInput';
import { isEmptyObject, validateIsEmpty } from '../../../../utils/validation';
import SelectMulti from '../../../../components/select-multi/SelectMulti';
import CourseList from '../list-course/CourseList';
import { useQueryDictionary } from '../../../../hooks/useQueryDictionary';
import Checkbox from 'src/components/checkbox/Checkbox';
import TagsForm from './tags-form/TagsForm';
import { auAllYearLevels, countryOptions, GROUP_ID_BY_COUNTRY, naAllGradeLevels } from '@constants';
import { useAddSchoolMutation, useEditSchoolMutation, SchoolDataFragmentDoc, useGenerateDashboardLinkMutation, School } from '@api/graphql-codegen'
import { useToast } from 'src/hooks/useToast';
import { logger } from '../../../../utils/logger';

type Props = {
  open: boolean;
  onClose: () => void;
  school?: School;
  account: Customer;
  programOptions: SelectOption[];
};

const emptyErrors = {
  name: '',
  courses: '',
};

function AddModal(props: Props) {
  const { open, onClose, school, account, programOptions } = props;
  const history = useHistory();
  const { successToast } = useToast();
  const [generateDashboardLink, { loading: generatingParentAppLink }] = useGenerateDashboardLinkMutation();
  const isEdit = !!school;

  const groupOptions = useQueryDictionary({ dictionary: 'ProgramTypes' });
  const coursesOptions = useQueryDictionary({ dictionary: 'Programs', isSkip: !isEdit });
  const [groups, setGroups] = useState<string[]>([]);
  const [dataCollections, setDataCollections] = useState<string[]>([]);
  const [grades, setGrades] = useState<string[]>([]);
  const [programs, setPrograms] = useState<string[]>([]);
  const [courses, setCourses] = useState<string[]>([]);
  const [disable, setDisable] = useState(false);
  const [disablePasswordLogin, setDisablePasswordLogin] = useState(false);
  const dataCollectionOptions: SelectOption[] = useMemo(() => {
    if (!account.dataCollections) return [];
    return account.dataCollections.map(a => ({
      label: a.name,
      value: a.id,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account.id]);

  useEffect(() => {
    setName(school?.name ?? '');
    const accountGroupId = GROUP_ID_BY_COUNTRY[account.country] ? [GROUP_ID_BY_COUNTRY[account.country]] : [];
    setGroups(school?.groups?.map(g => g?.id) ?? accountGroupId);
    
    // Inherit all survey types from the parent account instead of just "Research"
    if (isEdit) {
      setDataCollections(school?.dataCollections?.map(g => g?.id ?? "") ?? []);  
    } else if (account?.dataCollections) {
      // Set all data collections from the parent account
      setDataCollections(account.dataCollections.map(collection => collection.id));  
    }

    setBeError('');
    setCourses(school?.courses?.map(g => g?.id ?? "") ?? []);
    setEmails(school?.exitTicketCC?.split(',') ?? []);
    setPpqCC(school?.preProgramQuizCC?.split(',') ?? []);
    setDisable(school?.status === 'expired')
    setDisablePasswordLogin(school?.disabledPwLogin ?? false);
    setGrades(JSON.parse(school?.grades || '[]'));
    setPrograms(JSON.parse(school?.programs || '[]'));
  }, [school, account, isEdit]);

  const [addSchool, { loading }] = useAddSchoolMutation({
    onError(e) {
      setBeError(e.message);
    },
    onCompleted(){
      handleClose();
    },
    update(cache, res) {
      if (res.data?.addSchool) {
        cache.modify({
          id: `accountSchools({"accountId":"${account.id}"})`,
          fields: {
            schools(value) {
              return [...(value || []), res.data?.addSchool]
            }
          },
        });
      }
    }
  });

  const [editSchool, { loading: editLoading }] = useEditSchoolMutation({
    onError(e) {
      setBeError(e.message);
    },
    update(cache, response) {
      const u = response.data?.editSchool;
      if (u) {
        cache.writeFragment({
          id: cache.identify(u),
          data: {
            ...u,
          },
          fragment: SchoolDataFragmentDoc,
        });
      }
    },
    onCompleted() {
      handleClose();
    }
  });

  const [name, setName] = useState('');
  const [error, setError] = useState(emptyErrors);
  const [emails, setEmails] = useState<string[]>([]);
  const [ppqCC, setPpqCC] = useState<string[]>([]);
  const [beError, setBeError] = useState('');

  const handleChangeText = (setter: Dispatch<SetStateAction<string>>, key: keyof typeof emptyErrors) => (value: TextInputValue) => {
    setter(value as string);
    setError({
      ...error,
      [key]: '',
    });
  };

  const handleClose = () => {
    clearFields();
    onClose();
  };

  const clearFields = () => {
    setName('');
    setGroups([]);
  };

  const validate = useCallback(() => {
    const errors: any = {};

    if (validateIsEmpty(name)) {
      errors.name = 'Name is required';
    }

    return errors;
  }, [name]);

  const handleSave = useCallback(async (e: FormEvent) => {
    e.preventDefault();
    setBeError('');

    const errors = validate();

    if (isEmptyObject(errors)) {
      const result = await addSchool({
        variables: {
          input: {
            accountId: account.id,
            name,
            country: account.country,
            groups,
            dataCollections,
            programs,
            grades,
          }
        }
      })
      if (result.data?.addSchool) {
        history.push({ pathname: '/super/school/edit', state: { school: result.data?.addSchool, accountId: account.id, isEdit: true } });
      }
    } else {
      setError(errors);
    }
  }, [account.country, account.id, addSchool, dataCollections, groups, history, name, validate, programs, grades,]);

  const handleEdit = useCallback((e: FormEvent) => {
    e.preventDefault();
    setBeError('');

    const errors = validate();

    if (isEmptyObject((errors))) {
      editSchool({
        variables: {
          input: {
            accountId: account.id,
            id: school!.id,
            name,
            country: account.country,
            groups,
            dataCollections,
            courses,
            exitTicketCC: emails,
            preProgramQuizCC: ppqCC,
            status: disable ? 'expired' : 'active',
            disabledPwLogin: disablePasswordLogin,
            programs,
            grades,
          }
        }
      })
    } else {
      setError(errors);
    }
  }, [
    account.country,
    account.id,
    courses,
    dataCollections,
    disable,
    disablePasswordLogin,
    editSchool,
    emails,
    groups,
    name,
    ppqCC,
    school,
    validate,
    programs,
    grades
  ]);

  const goToEditSchool = useCallback(() => {
    history.push({ pathname: '/super/school/edit', state: { school, accountId: account.id, isEdit: true } })
  }, [account.id, history, school])

  const generateAndCopyParentAppLink = useCallback(async () => {
    if (school?.id) {
      try {
        const result = await generateDashboardLink({ variables: { input: { id: school?.id } } });
        if (result.data?.generateDashboardLink) {
          await navigator.clipboard.writeText(`${window.location.protocol}//${window.location.host}/parent/login?id=${result.data?.generateDashboardLink}`)
          successToast('Copied a Parent-App Link!');
        }
      } catch (e) {
        logger.info(e)
      }
    }
  }, [successToast, generateDashboardLink, school?.id]);

  const copyRegistrationLink = useCallback(async () => {
    await navigator.clipboard.writeText(`${window.location.protocol}//${window.location.host}/registration?id=${school?.id}&entity=sch`)
    successToast('Link copied successfully!');
  }, [successToast, school?.id])

  const handleChangeGroups = useCallback((options: string[]) => {
    setGroups(options);
  }, []);
  const handleChangeDataCollections = (options: string[]) => setDataCollections(options);
  const handleChangeCheckbox = (setter: Dispatch<SetStateAction<boolean>>) => (value: boolean) => setter(value);
  const handleChangeGrades = (options: string[]) => setGrades(options);
  const handleChangePrograms = (options: string[]) => setPrograms(options);

  return (
    <Modal open={open} onClose={handleClose} closeOnOverlayClick={false} className={styles.wrapper}>
      <div className={styles.title}>{isEdit ? 'Edit' : 'Add'} School</div>
      <form className={styles.form} onSubmit={isEdit ? handleEdit : handleSave}>
        <div className={styles.content}>
          <TextInput
            hasError={!!error.name}
            subTitle={error.name}
            placeholder={'Name'}
            inline={true}
            label={'Name'}
            id={'name'}
            value={name}
            onChange={handleChangeText(setName, 'name')}
          />
          <SelectMulti inline={true} label={'Groups'} options={groupOptions} onChange={handleChangeGroups} value={groups}/>
          {isEdit ? (
            <div className={styles.row}>
              <CourseList subTitle={error.courses} error={!!error.courses} inline={true} label={'Courses'}
                       options={coursesOptions}
                       value={courses} disabled={!coursesOptions || coursesOptions.length === 0}/>
            </div>
          ) : null}

          {isEdit ? <div className={styles.courseAction}>
            <div/>
            <Button disabled={editLoading} onClick={goToEditSchool} label={'View/Edit'}/>
            <div/>
          </div> : null}
          <div className={styles.row}>
            <SelectMulti label={'Data collection'} showSearch={false} options={dataCollectionOptions} onChange={handleChangeDataCollections}
                       value={dataCollections} inline={true}/>
          </div>
          <div className={styles.row}>
            <SelectMulti label={'Program'} showSearch={false} options={programOptions} onChange={handleChangePrograms}
                       value={programs} inline={true}/>
          </div>
          <div className={styles.row}>
            <SelectMulti label={'Age Group'} doNotSort={true} showSearch={false} options={account.country === countryOptions[1].value ? auAllYearLevels : naAllGradeLevels} onChange={handleChangeGrades}
                       value={grades} inline={true}/>
          </div>
          {isEdit ? <>
            <div className={styles.courseAction}>
              <div className={styles.headerLabel}>Status</div>
                <Checkbox disabled={false} id={'school'} checked={disable}
                      onChange={handleChangeCheckbox(setDisable)} label={'Disable School'}/>
              <div/>
            </div>
            <div className={styles.statusAction}>
              <div/>
              <div><div className={styles.status}>{school?.status === 'expired' ? 'Disabled' : 'Active'}</div></div>
              <div/>
            </div>
          </> : null}

          {isEdit ? (
            <>
              <div className={styles.ccAction}>
                <div className={styles.ccLabel}>Exit ticket CC</div>
                <TagsForm tags={emails} setTags={setEmails} />
              </div>
              <div className={styles.ccAction}>
                <div className={styles.ccLabel}>Pre-Program Quiz CC</div>
                <TagsForm tags={ppqCC} setTags={setPpqCC} />
              </div>
            </>
          ) : null}
          {Boolean(beError) && <div className={styles.error}>Error: {beError}</div>}
          {isEdit ? (
            <>
              <div className={styles.action}>
                <div />
                <div>
                  <Button disabled={editLoading || generatingParentAppLink} isLoading={generatingParentAppLink} label={'Copy a parent-app link '} outline className={styles.copyButton} onClick={generateAndCopyParentAppLink} />
                </div>
              </div>
              <div className={styles.action}>
                <div />
                <div>
                  <Button disabled={editLoading} label={'Copy registration link '} outline className={styles.copyButton} onClick={copyRegistrationLink} />
                </div>
              </div>
            </>
          ) : null}
          {isEdit && (
            <div className={styles.ccAction}>
              <div />
              <Checkbox disabled={false} id={'disablePasswordLogin'} checked={disablePasswordLogin}
                      onChange={handleChangeCheckbox(setDisablePasswordLogin)} label={'Restrict Login to SSO Only'}/>
            </div>
          ) }
          <div className={styles.action}>
            <div/>
            {isEdit ? <Button disabled={editLoading || groupOptions.length === 0} type={'submit'} label={'Save'}/> :
              <Button disabled={loading} type={'submit'} label={'Create'}/>}
          </div>
        </div>
      </form>
    </Modal>
  );
}

export default AddModal;
